import { DateTime } from 'luxon';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import type { TabPill } from '../../../components';
import { Tag, TabPills } from '../../../components';
import type { ActivityCategory, ActivityLog } from './types';
import { CATEGORIES } from './types';

interface Props {
  logs: ActivityLog[];
  /* The stored history is still loading. */
  loading?: boolean;
  /* The stored history could not be loaded; live entries still show. */
  error?: boolean;
  onRetry?: () => void;
}

type Filter = ActivityCategory | 'all';

const CATEGORY_BY_KEY = new Map(CATEGORIES.map((c) => [c.key, c]));

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 13px;
  min-height: 34px;
  font-size: 15px;
  font-weight: 700;
  color: var(--white);
`;

// Drop the group chrome and give each pill its own outline instead.
const FilterPills = styled(TabPills)`
  border: none;
  background: transparent;
  padding: 0;
  gap: 6px;
  margin-bottom: 14px;

  button {
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 999px;
  }

  button.active {
    border-color: rgba(120, 130, 255, 0.4);
  }

  .count {
    padding: 0;
    color: rgba(176, 186, 209, 0.7);
  }
`;

const Timeline = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  overflow: hidden;
  background: rgba(17, 27, 51, 0.3);
`;

const TimelineHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);

  .label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.6);
  }

  .stats {
    display: inline-flex;
    gap: 12px;
    font-size: 11px;
    color: rgba(176, 186, 209, 0.6);

    b {
      color: var(--white);
    }
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: start;
  padding: 13px 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);

  &:last-child {
    border-bottom: none;
  }

  .badge {
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .title {
    font-size: 13px;
    font-weight: 700;
    color: var(--white);
  }

  .detail {
    margin-top: 3px;
    font-size: 12px;
    color: rgba(205, 214, 234, 0.78);
  }

  .source {
    margin-top: 3px;
    font-size: 11px;
    color: rgba(176, 186, 209, 0.55);
  }

  .time {
    font: 500 11px / 1.7 'JetBrains Mono', monospace;
    color: rgba(176, 186, 209, 0.6);
    white-space: nowrap;
  }
`;

const Empty = styled.div`
  padding: 22px 15px;
  text-align: center;
  font-size: 13px;
  color: rgba(176, 186, 209, 0.55);
`;

const HistoryError = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  font-size: 12px;
  color: var(--danger);

  button {
    border: none;
    background: none;
    padding: 0;
    font: inherit;
    font-weight: 700;
    color: var(--white);
    cursor: pointer;
  }
`;

const formatTime = (iso: string): string => {
  const dt = DateTime.fromISO(iso);

  return dt.isValid ? dt.toFormat('LLL d, h:mm a') : '';
};

// Sources write timestamps with different precision (Mux uses microseconds), so compare instants, not text.
const toMillis = (iso: string): number => {
  const dt = DateTime.fromISO(iso);

  return dt.isValid ? dt.toMillis() : 0;
};

export const ActivityTab = ({ logs, loading = false, error = false, onRetry }: Props): ReactElement => {
  const [filter, setFilter] = useState<Filter>('all');

  const counts = useMemo(() => {
    const byCategory = new Map<ActivityCategory, number>();

    for (const log of logs) {
      byCategory.set(log.category, (byCategory.get(log.category) ?? 0) + 1);
    }

    return byCategory;
  }, [logs]);

  const visible = useMemo(
    () =>
      logs
        .filter((log) => filter === 'all' || log.category === filter)
        .sort((a, b) => toMillis(b.timestamp) - toMillis(a.timestamp)),
    [logs, filter]
  );

  const pills: TabPill[] = [
    { key: 'all', label: 'All', count: logs.length },
    ...CATEGORIES.map((c) => ({
      key: c.key,
      label: c.label,
      count: counts.get(c.key) ?? 0
    }))
  ].map((pill) => ({
    ...pill,
    active: pill.key === filter,
    onClick: (): void => {
      setFilter(pill.key as Filter);
    }
  }));

  return (
    <div>
      <SectionTitle>Activity</SectionTitle>

      <FilterPills small darker pills={pills} />

      <Timeline>
        <TimelineHead>
          <span className='label'>Event timeline</span>

          <span className='stats'>
            <span>
              Total <b>{logs.length}</b>
            </span>
            {CATEGORIES.filter((c) => counts.has(c.key)).map((c) => (
              <span key={c.key}>
                {c.label} <b>{counts.get(c.key)}</b>
              </span>
            ))}
          </span>
        </TimelineHead>

        {error ? (
          <HistoryError>
            <span>Couldn&apos;t load past activity. New entries still show here.</span>
            {onRetry ? (
              <button type='button' onClick={onRetry}>
                Retry
              </button>
            ) : null}
          </HistoryError>
        ) : null}

        {visible.length === 0 ? (
          <Empty>
            {loading ? 'Loading activity…' : filter === 'all' ? 'No activity yet.' : 'No activity from this source yet.'}
          </Empty>
        ) : (
          visible.map((log) => {
            const category = CATEGORY_BY_KEY.get(log.category);

            return (
              <Row key={log.id}>
                <div>
                  <Tag size='small' className={`badge ${category?.className ?? ''}`}>
                    {category?.label ?? log.category}
                  </Tag>
                </div>

                <div>
                  <div className='title'>{log.title}</div>
                  {log.detail !== undefined && <div className='detail'>{log.detail}</div>}
                  <div className='source'>{log.source}</div>
                </div>

                <span className='time'>{formatTime(log.timestamp)}</span>
              </Row>
            );
          })
        )}
      </Timeline>
    </div>
  );
};
